import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Radio, Music2, BookOpenText, ListMusic, BicepsFlexed, Upload, ShieldCheck, LogOut, LogIn, UserPlus, Home, Disc3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import StationClock from "./StationClock";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
        isActive
          ? "bg-[#C5A059]/10 text-[#FDFBF7] border border-[#C5A059]/30"
          : "text-[#A1A4AB] hover:text-[#FDFBF7] hover:bg-white/[0.03] border border-transparent"
      }`
    }
  >
    <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
    <span className="text-sm">{label}</span>
  </NavLink>
);

const SectionLabel = ({ children }) => (
  <div className="overline text-[#6C707A] px-4 mt-6 mb-2">{children}</div>
);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="hidden lg:flex fixed top-0 left-0 w-64 flex-col bg-[#060709] border-r border-[#22262E] z-40" style={{ height: "calc(100vh - 6rem)" }}>
      <div className="px-6 py-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C5A059] to-[#8B6F35] flex items-center justify-center text-[#060709] font-serif font-bold text-xl shadow-[0_0_18px_rgba(197,160,89,0.35)]">
            Y
          </div>
          <div className="leading-tight">
            <div className="font-serif text-[#FDFBF7] text-lg">Yah-Wave</div>
            <div className="overline text-[#C5A059]">Radio</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <SectionLabel>Discover</SectionLabel>
        <div className="flex flex-col gap-1">
          <NavItem to="/" icon={Home} label="Home" testid="nav-home" />
          <NavItem to="/stations" icon={Disc3} label="Stations" testid="nav-stations" />
          <NavItem to="/radio" icon={Radio} label="Live Radio" testid="nav-radio" />
          <NavItem to="/music" icon={Music2} label="Music" testid="nav-music" />
          <NavItem to="/sermons" icon={BookOpenText} label="Sermons & Teachings" testid="nav-sermons" />
        </div>

        {user && (
          <>
            <SectionLabel>Your Library</SectionLabel>
            <div className="flex flex-col gap-1">
              <NavItem to="/playlists" icon={ListMusic} label="Playlists" testid="nav-playlists" />
              <NavItem to="/favorites" icon={BicepsFlexed} label="Favorites" testid="nav-favorites" />
            </div>

            <SectionLabel>Contribute</SectionLabel>
            <div className="flex flex-col gap-1">
              <NavItem to="/submit" icon={Upload} label="Submit Music" testid="nav-submit" />
              <NavItem to="/my-submissions" icon={ListMusic} label="My Submissions" testid="nav-my-submissions" />
            </div>
          </>
        )}

        {isAdmin && (
          <>
            <SectionLabel>Admin</SectionLabel>
            <div className="flex flex-col gap-1">
              <NavItem to="/admin" icon={ShieldCheck} label="Admin Dashboard" testid="nav-admin" />
            </div>
          </>
        )}
      </nav>

      <div className="p-4 border-t border-[#22262E] space-y-3">
        <StationClock />
        {user ? (
          <div className="space-y-2">
            <div className="px-2">
              <div className="text-sm text-[#FDFBF7] truncate" data-testid="sidebar-username">{user.name}</div>
              <div className="text-xs text-[#6C707A] truncate">{user.email}</div>
              {isAdmin && <div className="overline text-[#C5A059] mt-1">Administrator</div>}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-2 justify-center btn-secondary text-sm border-[#C5A059]/30 hover:border-[#C5A059] py-2"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Link to="/login" data-testid="sidebar-login-link" className="btn-secondary text-sm justify-center">
              <LogIn className="w-4 h-4" strokeWidth={1.5} /> Sign in
            </Link>
            <Link to="/register" data-testid="sidebar-register-link" className="btn-primary text-sm justify-center">
              <UserPlus className="w-4 h-4" strokeWidth={1.5} /> Create account
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
